import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

import LandingPage from "./pages/LandingPage";
import OrganizationsPage from "./pages/OrganizationsPage";
import { LoginPage, RegisterPage } from "./pages/AuthPages";
import EnterpriseDashboard from "./pages/EnterpriseDashboard";
import WorkforceHeatmap from "./pages/WorkforceHeatmap";
import WorkerDiagnostic from "./pages/WorkerDiagnostic";
import BuilderCore from "./pages/BuilderCore";
import BoardReport from "./pages/BoardReport";
import CohortManagement from "./pages/CohortManagement";
import AssessmentsPage from "./pages/AssessmentsPage";
import ParticipantDashboard from "./pages/ParticipantDashboard";
import TaskPage from "./pages/TaskPage";
import CommunityHub from "./pages/CommunityHub";
import MyMentor from "./pages/MyMentor";
import AdminWorkspacesPage, { AdminLayout } from "./pages/AdminPages";
import AdminWorkspaceDetailPage from "./pages/AdminWorkspaceDetailPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminImpersonationsPage from "./pages/AdminImpersonationsPage";
import AdminAuditPage from "./pages/AdminAuditPage";
import { AdminModulesPage, AdminArchetypesPage } from "./pages/AdminPlaceholders";
import AdminHealthPage from "./pages/AdminHealthPage";
import AdminJDNormalizerPage from "./pages/AdminJDNormalizerPage";
import WorkspaceSetupWizard from "./pages/WorkspaceSetupWizard";
import WorkspaceContextPage from "./pages/WorkspaceContextPage";
import WorkspaceCommunicationsPage from "./pages/WorkspaceCommunicationsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import FirstLoginWalkthrough from "./components/FirstLoginWalkthrough";
import AdminEquipPage from "./pages/AdminEquipPage";
import AdminPlatformPage from "./pages/AdminPlatformPage";
import AdminScoringPage from "./pages/AdminScoringPage";
import CandidateAssessmentPage from "./pages/CandidateAssessmentPage";
import CandidateReviewPage from "./pages/CandidateReviewPage";
import ManagerValidationPage from "./pages/ManagerValidationPage";
import AdminPartnersPage from "./pages/AdminPartnersPage";
import PartnerLayout from "./pages/partner/PartnerLayout";
import PartnerDashboard from "./pages/partner/PartnerDashboard";
import PartnerCompaniesPage from "./pages/partner/PartnerCompaniesPage";
import PartnerLibraryPage from "./pages/partner/PartnerLibraryPage";
import PartnerAuditPage from "./pages/partner/PartnerAuditPage";
import PartnerBillingPage from "./pages/partner/PartnerBillingPage";
import PartnerSettingsPage from "./pages/partner/PartnerSettingsPage";
import ProvisionCompanyWizard from "./pages/partner/ProvisionCompanyWizard";
import OperatorLayout from "./pages/operator/OperatorLayout";
import OperatorDashboard from "./pages/operator/OperatorDashboard";
import CreateCompanyWizard from "./pages/operator/CreateCompanyWizard";
import OperatorSettingsPage from "./pages/operator/OperatorSettingsPage";
import ActingWorkspaceBanner from "./components/ActingWorkspaceBanner";

const ProtectedRoute = ({ children, allowedRoles }) => {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (allowedRoles && !allowedRoles.includes(user.role)) {
        if (user.role === "platform_admin") return <Navigate to="/admin" replace />;
        if (user.role === "partner_owner" || user.role === "partner_member") return <Navigate to="/partner" replace />;
        if (user.role === "company_operator") return <Navigate to="/operator" replace />;
        if (user.role === "enterprise_admin" || user.role === "super_admin" || user.role === "enterprise_executive") return <Navigate to="/enterprise" replace />;
        if (user.role === "participant") return <Navigate to="/learn" replace />;
        if (user.role === "mentor") return <Navigate to="/learn" replace />;
        return <Navigate to="/" replace />;
    }
    return children;
};

const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    if (user) {
        if (user.role === "platform_admin") return <Navigate to="/admin" replace />;
        if (user.role === "partner_owner" || user.role === "partner_member") return <Navigate to="/partner" replace />;
        if (user.role === "enterprise_admin" || user.role === "super_admin" || user.role === "enterprise_executive") return <Navigate to="/enterprise" replace />;
        if (user.role === "participant" || user.role === "employee") return <Navigate to="/learn" replace />;
        if (user.role === "mentor") return <Navigate to="/learn" replace />;
    }
    return children;
};

function AppRoutes() {
    return (
        <>
            <ActingWorkspaceBanner />
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/organizations" element={<OrganizationsPage />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

            {/* Platform Admin */}
            <Route path="/admin" element={<ProtectedRoute allowedRoles={["platform_admin"]}><AdminLayout /></ProtectedRoute>}>
                <Route index element={<AdminWorkspacesPage />} />
                <Route path="workspace/:id" element={<AdminWorkspaceDetailPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="modules" element={<AdminModulesPage />} />
                <Route path="archetypes" element={<AdminArchetypesPage />} />
                <Route path="scoring" element={<AdminScoringPage />} />
                <Route path="equip" element={<AdminEquipPage />} />
                <Route path="platform" element={<AdminPlatformPage />} />
                <Route path="jd-normalizer" element={<AdminJDNormalizerPage />} />
                <Route path="workspaces/new" element={<WorkspaceSetupWizard />} />
                <Route path="workspace/:id/context" element={<WorkspaceContextPage />} />
                <Route path="workspace/:id/communications" element={<WorkspaceCommunicationsPage />} />
                <Route path="audit" element={<AdminAuditPage />} />
                <Route path="impersonations" element={<AdminImpersonationsPage />} />
                <Route path="health" element={<AdminHealthPage />} />
                <Route path="partners" element={<AdminPartnersPage />} />
            </Route>

            {/* Partner routes */}
            <Route path="/partner" element={<ProtectedRoute allowedRoles={["partner_owner", "partner_member"]}><PartnerLayout /></ProtectedRoute>}>
                <Route index element={<PartnerDashboard />} />
                <Route path="companies" element={<PartnerCompaniesPage />} />
                <Route path="companies/new" element={<ProvisionCompanyWizard />} />
                <Route path="library" element={<PartnerLibraryPage />} />
                <Route path="audit" element={<PartnerAuditPage />} />
                <Route path="billing" element={<PartnerBillingPage />} />
                <Route path="settings" element={<PartnerSettingsPage />} />
            </Route>

            {/* Company operator routes */}
            <Route path="/operator" element={<ProtectedRoute allowedRoles={["company_operator"]}><OperatorLayout /></ProtectedRoute>}>
                <Route index element={<OperatorDashboard />} />
                <Route path="companies/new" element={<CreateCompanyWizard />} />
                <Route path="settings" element={<OperatorSettingsPage />} />
            </Route>

            {/* Public candidate assessment */}
            <Route path="/take/:token" element={<CandidateAssessmentPage />} />
            <Route path="/review/:token" element={<CandidateReviewPage />} />
            <Route path="/mv/:token" element={<ManagerValidationPage />} />

            {/* Enterprise */}
            <Route path="/enterprise" element={<ProtectedRoute allowedRoles={["enterprise_admin", "super_admin", "enterprise_executive", "platform_admin", "business_unit_lead", "business_unit_hrbp"]}><EnterpriseDashboard /></ProtectedRoute>} />
            <Route path="/enterprise/heatmap" element={<ProtectedRoute allowedRoles={["enterprise_admin", "super_admin", "enterprise_executive", "platform_admin", "business_unit_lead", "business_unit_hrbp"]}><WorkforceHeatmap /></ProtectedRoute>} />
            <Route path="/enterprise/worker/:workerId" element={<ProtectedRoute allowedRoles={["enterprise_admin", "super_admin", "enterprise_executive", "platform_admin", "business_unit_lead", "business_unit_hrbp", "manager"]}><WorkerDiagnostic /></ProtectedRoute>} />
            <Route path="/enterprise/builder-core" element={<ProtectedRoute allowedRoles={["enterprise_admin", "super_admin", "enterprise_executive"]}><BuilderCore /></ProtectedRoute>} />
            <Route path="/enterprise/cohorts" element={<ProtectedRoute allowedRoles={["enterprise_admin", "super_admin", "enterprise_executive", "platform_admin", "business_unit_lead", "business_unit_hrbp"]}><CohortManagement /></ProtectedRoute>} />
            <Route path="/enterprise/assessments" element={<ProtectedRoute allowedRoles={["enterprise_admin", "super_admin", "enterprise_executive", "platform_admin", "business_unit_lead", "business_unit_hrbp"]}><AssessmentsPage /></ProtectedRoute>} />
            <Route path="/enterprise/settings/context" element={<ProtectedRoute allowedRoles={["enterprise_admin", "enterprise_executive", "platform_admin"]}><WorkspaceContextPage /></ProtectedRoute>} />
            <Route path="/enterprise/settings/communications" element={<ProtectedRoute allowedRoles={["enterprise_admin", "enterprise_executive", "platform_admin"]}><WorkspaceCommunicationsPage /></ProtectedRoute>} />
            <Route path="/enterprise/report" element={<ProtectedRoute allowedRoles={["enterprise_admin", "super_admin", "enterprise_executive", "platform_admin", "business_unit_lead"]}><BoardReport /></ProtectedRoute>} />

            {/* Participant / Mentor */}
            <Route path="/learn" element={<ProtectedRoute allowedRoles={["participant", "mentor", "employee"]}><ParticipantDashboard /></ProtectedRoute>} />
            <Route path="/learn/domain/:domainId/task/:taskId" element={<ProtectedRoute allowedRoles={["participant", "mentor", "employee"]}><TaskPage /></ProtectedRoute>} />
            <Route path="/learn/mentor" element={<ProtectedRoute allowedRoles={["participant", "employee"]}><MyMentor /></ProtectedRoute>} />
            <Route path="/community" element={<ProtectedRoute allowedRoles={["participant", "mentor", "enterprise_admin", "super_admin", "enterprise_executive", "employee"]}><CommunityHub /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </>
    );
}

function SessionExpiredHandler() {
    const navigate = useNavigate();
    useEffect(() => {
        const handler = () => {
            toast.error("Your session expired — please log in again.", { duration: 6000 });
            const currentPath = window.location.pathname + window.location.search;
            const onLogin = currentPath.startsWith("/login");
            if (!onLogin) {
                navigate(`/login?next=${encodeURIComponent(currentPath)}`, { replace: true });
            }
        };
        window.addEventListener("realloc:session-expired", handler);
        return () => window.removeEventListener("realloc:session-expired", handler);
    }, [navigate]);
    return null;
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <SessionExpiredHandler />
                <AppRoutes />
                <FirstLoginWalkthrough />
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
